import { useEffect, useState, useRef } from "react";
import { Trophy, Sparkles, Check, HandMetal, AlertTriangle, Vote, Settings2, Gift, Plus, Trash2, Users, X, ChevronRight, Sliders, BookOpen, Download, CalendarClock, RefreshCw, Loader2 } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { BrutalButton, SectionTitle, EmptyState } from "@/components/Brutal";
import WheelOfFortune from "@/components/WheelOfFortune";
import { fireConfetti } from "@/components/Confetti";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import AllowanceWizard from "@/components/AllowanceWizard";

const WEEKDAYS = ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"];
const KIND_META = {
  task_complete: { icon: Check, label: "Taak afgevinkt", pts: "+50", color: "bg-brand-green" },
  task_pickup: { icon: HandMetal, label: "Taak opgepakt", pts: "+75", color: "bg-brand-blue text-white" },
  poll_vote: { icon: Vote, label: "Poll beantwoord", pts: "+25", color: "bg-brand-violet text-white" },
  task_overdue: { icon: AlertTriangle, label: "Te laat", pts: "-100", color: "bg-brand-yellow" },
  postpone_penalty: { icon: CalendarClock, label: "Te vaak uitgesteld", pts: "", color: "bg-brand-orange text-white" },
  manual_adjust: { icon: Sliders, label: "Handmatige aanpassing", pts: "", color: "bg-brand-ink text-white" },
};
const MEDALS = ["🥇", "🥈", "🥉"];
const WINLOG_ICON = { spin: "🎡", week: "💶", month: "🏆", winner: "🏅" };

export default function Punten() {
  const { user } = useAuth();
  const t = useT();
  const admin = isAdmin(user);
  const [data, setData] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const savedRef = useRef(null);
  const [saveState, setSaveState] = useState("idle");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [cfg, setCfg] = useState({ enabled: false, period: "week", weekday: 0, prizes: [], participant_mode: "all", participant_group: null, week_threshold: 0, week_reward: "", week_title: "Zakgeld", week_allowance_amount: 0, month_threshold: 0, month_reward: "", month_title: "Extra prijs", month_bonus_spin_points: 0, wheel_enabled: false, wheel_mode: "always", wheel_award: "winner", wheel_prizes: [], not_done_penalty: -100, smart_threshold: false, smart_threshold_pct: 70, away_reduction_pct: 100, catchup_enabled: false, catchup_pool: [], catchup_reminder_days: 1 });
  const [groups, setGroups] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [reflection, setReflection] = useState({ items: [], since_days: 30 });
  const [myReflection, setMyReflection] = useState(null);
  const [reflectDays, setReflectDays] = useState(30);
  const [payouts, setPayouts] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [histFilter, setHistFilter] = useState(null);
  const [weekProg, setWeekProg] = useState(null);
  const [adjUser, setAdjUser] = useState("");
  const [adjPoints, setAdjPoints] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [wheelOpen, setWheelOpen] = useState(false);
  const [grantUser, setGrantUser] = useState("");
  const [spinResults, setSpinResults] = useState([]);
  const [creditSpinOpen, setCreditSpinOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [winlog, setWinlog] = useState([]);
  const [uitlegBusy, setUitlegBusy] = useState(false);

  const openUitleg = async () => {
    try {
      const { data } = await api.get("/rewards/uitleg-pdf/status");
      window.open(data.url || "/slackr-puntensysteem.pdf", "_blank");
    } catch { window.open("/slackr-puntensysteem.pdf", "_blank"); }
  };

  const regenUitleg = async () => {
    setUitlegBusy(true);
    toast.info(t("Uitleg-PDF wordt gemaakt met jullie instellingen… dit duurt ±1 minuut"));
    try {
      await api.post("/rewards/uitleg-pdf/regenerate");
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const { data } = await api.get("/rewards/uitleg-pdf/status");
          if (data.ready) {
            clearInterval(poll); setUitlegBusy(false);
            toast.success(t("Uitleg-PDF is bijgewerkt! 📘"));
            window.open(data.url, "_blank");
          } else if (data.status === "error" || tries > 45) {
            clearInterval(poll); setUitlegBusy(false);
            toast.error(t("Genereren mislukt, probeer het opnieuw"));
          }
        } catch { /* blijf pollen */ }
      }, 3000);
    } catch (e) { setUitlegBusy(false); toast.error(t("Kon niet starten")); }
  };

  const openSpelregels = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.get("/rewards/spelregels.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(t("Kon spelregels niet laden")); }
    finally { setPdfBusy(false); }
  };

  const downloadSpelregels = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.get("/rewards/spelregels.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "slackr-spelregels.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(t("Kon spelregels niet laden")); }
    finally { setPdfBusy(false); }
  };

  const loadSpinResults = () => api.get("/rewards/spins/results").then(({ data }) => setSpinResults(data)).catch(() => {});
  const loadWinlog = () => api.get("/rewards/winlog").then(({ data }) => setWinlog(data)).catch(() => {});

  const grantSpin = async (random) => {
    if (!random && !grantUser) return toast.error(t("Kies een persoon"));
    try {
      const { data } = await api.post("/rewards/spins/grant", random ? { random: true } : { user_id: grantUser });
      toast.success(`${data.name}: ${data.credits} ${t("spin(s) te goed")} 🎡`);
      load(); loadSpinResults();
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const doCreditSpin = async (prize) => {
    try {
      const { data } = await api.post("/rewards/spins/spin", { prize });
      toast.success(`🎉 ${t("Gewonnen")}: ${prize}`);
      setTimeout(() => { load(); if (admin) loadSpinResults(); }, 300);
      return data;
    } catch (e) { toast.error(e.response?.data?.detail || t("Draaien mislukt")); }
  };

  const toggleClaim = async (rid) => {
    try { await api.post(`/rewards/spins/results/${rid}/claim`); loadSpinResults(); }
    catch (e) { toast.error(t("Mislukt")); }
  };

  const loadPenalties = () => api.get("/rewards/penalties").then(({ data }) => setPenalties(data)).catch(() => {});
  const loadReflection = (days = reflectDays) => api.get(`/rewards/reflection?days=${days}`).then(({ data }) => setReflection(data)).catch(() => {});
  const loadMyReflection = (days = reflectDays) => api.get(`/rewards/reflection/me?days=${days}`).then(({ data }) => setMyReflection(data)).catch(() => {});
  const changeReflectDays = (days) => { setReflectDays(days); loadMyReflection(days); if (admin) loadReflection(days); };
  const loadWeekProg = () => api.get("/rewards/week-progress").then(({ data }) => setWeekProg(data)).catch(() => {});
  const toggleAway = async () => { try { await api.post("/rewards/away", { away: !weekProg?.away }); loadWeekProg(); } catch { toast.error(t("Mislukt")); } };
  const claimCatchup = async (title) => { try { const { data } = await api.post("/rewards/catchup/claim", { title }); toast.success(`${t("Klus opgepakt")} · +${data.points} ${t("punten")} — ${t("vink 'm af bij Taken")}`); } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); } };

  const openBreakdown = async (s) => {
    const mine = s.user_id === user?.user_id;
    if (!admin && !mine) return;
    setDetailLoading(true);
    setDetail({ user_id: s.user_id, name: s.name, total: s.points, events: null });
    try {
      const { data } = await api.get(`/rewards/breakdown/${s.user_id}`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Kon puntenopbouw niet laden"));
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const load = async () => {
    const { data } = await api.get("/rewards");
    setData(data);
    api.get("/rewards/allowance/history").then(({ data }) => setPayouts(data.items || [])).catch(() => {});
    // Verse spin verdiend? Rad automatisch openen met confetti (één keer per nieuwe credit).
    try {
      const uid = user?.user_id || user?.id || "me";
      const key = `slackr_spin_seen_${uid}`;
      const seen = parseInt(localStorage.getItem(key) || "0", 10);
      const credits = data.spin_credits || 0;
      const spinnable = data.wheel?.enabled && (data.wheel?.prizes?.length > 1);
      if (spinnable && credits > seen) {
        fireConfetti();
        setCreditSpinOpen(true);
      }
      localStorage.setItem(key, String(credits));
    } catch (e) { /* noop */ }
    const nextCfg = { enabled: data.enabled, period: data.period, weekday: data.weekday, prizes: data.prizes || [],
      participant_mode: data.participant_mode || "all", participant_group: data.participant_group || null,
      week_threshold: data.week_threshold || 0, week_reward: data.week_reward || "", week_title: data.week_title || "Zakgeld", week_allowance_amount: data.week_allowance_amount || 0,
      month_threshold: data.month_threshold || 0, month_reward: data.month_reward || "", month_title: data.month_title || "Extra prijs", month_bonus_spin_points: data.month_bonus_spin_points || 0,
      wheel_enabled: data.wheel?.enabled || false, wheel_mode: data.wheel?.mode || "always", wheel_award: data.wheel?.award || "winner", wheel_prizes: data.wheel?.prizes || [], not_done_penalty: data.not_done_penalty ?? -100,
      smart_threshold: data.smart_threshold || false, smart_threshold_pct: data.smart_threshold_pct ?? 70, away_reduction_pct: data.away_reduction_pct ?? 100, catchup_enabled: data.catchup_enabled || false, catchup_pool: data.catchup_pool || [], catchup_reminder_days: data.catchup_reminder_days ?? 1 };
    setCfg(nextCfg);
    savedRef.current = JSON.stringify(nextCfg);
  };
  useEffect(() => { load(); loadWinlog(); loadMyReflection(); loadWeekProg(); }, []);
  useEffect(() => {
    if (!admin || savedRef.current === null) return;
    const snap = JSON.stringify(cfg);
    if (snap === savedRef.current) return;
    setSaveState("saving");
    const id = setTimeout(async () => {
      try { await api.put("/rewards/config", cfg); savedRef.current = snap; setSaveState("saved"); }
      catch { setSaveState("idle"); toast.error(t("Opslaan mislukt")); }
    }, 700);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, admin]);
  useEffect(() => {
    if (admin && searchParams.get("zakgeld") === "1") {
      setWizardOpen(true);
      searchParams.delete("zakgeld");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, searchParams]);
  useEffect(() => {
    if (admin) {
      api.get("/groups").then(({ data }) => setGroups(data)).catch(() => {});
      loadPenalties();
      loadReflection();
      loadSpinResults();
    }
  }, [admin]);

  const doAdjust = async () => {
    const pts = parseInt(adjPoints, 10);
    if (!adjUser) return toast.error(t("Kies een persoon"));
    if (!pts) return toast.error(t("Vul een aantal punten in (bv. 50 of -25)"));
    try {
      await api.post("/rewards/adjust", { user_id: adjUser, points: pts, reason: adjReason.trim() });
      toast.success(t("Punten aangepast"));
      setAdjPoints(""); setAdjReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const forgive = async (ev) => {
    try {
      await api.post(`/rewards/penalties/${ev.event_id}/forgive`);
      toast.success(t("Volledige punten toegekend 🎉"));
      loadPenalties(); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Mislukt")); }
  };

  const addPrize = () => setCfg((c) => ({ ...c, prizes: [...(c.prizes || []), { type: "threshold", threshold: 500, description: "" }] }));
  const updPrize = (i, patch) => setCfg((c) => ({ ...c, prizes: c.prizes.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));
  const delPrize = (i) => setCfg((c) => ({ ...c, prizes: c.prizes.filter((_, j) => j !== i) }));

  const addWheelPrize = () => setCfg((c) => ({ ...c, wheel_prizes: [...(c.wheel_prizes || []), { label: "", weight: 0 }] }));
  const updWheelPrize = (i, patch) => setCfg((c) => ({ ...c, wheel_prizes: c.wheel_prizes.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));
  const delWheelPrize = (i) => setCfg((c) => ({ ...c, wheel_prizes: c.wheel_prizes.filter((_, j) => j !== i) }));

  if (!data) return <div className="p-6 text-center text-brand-ink/50">{t("Laden…")}</div>;

  const nextReset = data.next_reset ? new Date(data.next_reset).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" }) : null;
  const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "long" }) : null;
  const fmtFull = (iso) => iso ? new Date(iso).toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" }) : null;
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : null;
  const periodFrom = fmtDay(data.period_start);
  const dayBefore = (iso) => { if (!iso) return null; const d = new Date(iso); d.setDate(d.getDate() - 1); return d.toLocaleDateString("nl-NL", { day: "numeric", month: "long" }); };
  const periodTo = dayBefore(data.next_reset);

  return (
    <div className="space-y-6" data-testid="punten-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>{t("Punten & ranglijst")}</SectionTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="spelregels-btn" onClick={openSpelregels} disabled={pdfBusy}
            className="brutal-sm brutal-press rounded-full pl-3 pr-4 py-2 bg-brand-yellow flex items-center gap-1.5 text-sm font-black disabled:opacity-60">
            <BookOpen className="w-4 h-4" strokeWidth={2.5} />{pdfBusy ? t("Even geduld…") : t("Spelregels")}
          </button>
          <button data-testid="spelregels-download-btn" onClick={downloadSpelregels} disabled={pdfBusy} title={t("Download spelregels")}
            className="brutal-sm brutal-press rounded-full p-2.5 bg-white disabled:opacity-60">
            <Download className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <button data-testid="uitleg-kinderen-btn" onClick={openUitleg} title={t("Uitleg voor kinderen (PDF)")}
            className="brutal-sm brutal-press rounded-full pl-3 pr-4 py-2 bg-brand-pink text-white flex items-center gap-1.5 text-sm font-black">
            <BookOpen className="w-4 h-4" strokeWidth={2.5} />{t("Uitleg")}
          </button>
          {admin && (
            <button data-testid="uitleg-regen-btn" onClick={regenUitleg} disabled={uitlegBusy} title={t("Uitleg opnieuw maken met jullie punten/bedragen")}
              className="brutal-sm brutal-press rounded-full p-2.5 bg-white disabled:opacity-60">
              <RefreshCw className={`w-5 h-5 ${uitlegBusy ? "animate-spin" : ""}`} strokeWidth={2.5} />
            </button>
          )}
          {admin && (
            <button data-testid="rewards-config-btn" onClick={() => setShowCfg((v) => !v)}
              className={`brutal-sm brutal-press rounded-full p-2.5 ${showCfg ? "bg-brand-violet text-white" : "bg-white"}`}>
              <Settings2 className="w-5 h-5" strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>

      <AllowanceWizard open={wizardOpen} onClose={() => setWizardOpen(false)} weekTitle={cfg.week_title} />

      {admin && cfg.enabled && cfg.week_allowance_amount > 0 && (
        <button data-testid="allowance-payout-btn" onClick={() => setWizardOpen(true)}
          className="w-full brutal brutal-press rounded-2xl p-4 bg-white flex items-center justify-between gap-3">
          <span className="flex items-center gap-3">
            <span className="text-2xl">💶</span>
            <span className="text-start">
              <span className="font-head font-black text-lg block leading-tight">{cfg.week_title || t("Zakgeld")} {t("uitbetalen")}</span>
              <span className="text-sm font-semibold text-brand-ink/60">{t("Rond de week af en keer pro-rato uit")}</span>
            </span>
          </span>
          <span className="brutal-sm rounded-full px-4 py-2 bg-brand-violet text-white font-black shrink-0">{t("Openen")}</span>
        </button>
      )}

      {admin && cfg.enabled && (cfg.period === "week" || cfg.period === "combi") && cfg.week_allowance_amount > 0 && !data.allowance_paid_this_week && ((new Date().getDay() + 6) % 7) === cfg.weekday && (
        <div data-testid="allowance-due-banner" className="brutal rounded-2xl p-4 bg-gradient-to-r from-brand-violet to-brand-pink text-white flex items-center justify-between gap-3 flex-wrap animate-pop">
          <div className="flex items-center gap-2">
            <span className="text-2xl">💶</span>
            <div>
              <p className="font-head font-black text-lg leading-tight">{t("Tijd om het")} {cfg.week_title || t("Zakgeld")} {t("te bepalen!")}</p>
              <p className="text-sm font-semibold opacity-90">{t("De week is om — rond af en keer pro-rato uit.")}</p>
            </div>
          </div>
          <button data-testid="allowance-due-btn" onClick={() => setWizardOpen(true)}
            className="brutal-sm brutal-press rounded-full px-5 py-2.5 bg-white text-brand-violet font-black flex items-center gap-2 shrink-0">
            <Trophy className="w-5 h-5" strokeWidth={2.5} />{t("Bepaal zakgeld")}
          </button>
        </div>
      )}

      {admin && payouts.length > 0 && (
        <div className="brutal rounded-2xl bg-white overflow-hidden" data-testid="payout-history">
          <button onClick={() => setHistOpen(!histOpen)} data-testid="payout-history-toggle" className="w-full flex items-center justify-between p-4 font-head font-black text-lg">
            <span className="flex items-center gap-2">💶 {t("Zakgeld-historie")}</span>
            <span className={`transition-transform ${histOpen ? "rotate-180" : ""}`}>▾</span>
          </button>
          {histOpen && (
            <div className="px-4 pb-4 space-y-3">
              {histFilter && (
                <div className="flex items-center justify-between gap-2 bg-brand-violet/10 rounded-xl px-3 py-2" data-testid="payout-filter-active">
                  <span className="text-sm font-bold">{t("Alleen")}: <span className="text-brand-violet">{histFilter}</span></span>
                  <button data-testid="payout-filter-clear" onClick={() => setHistFilter(null)} className="text-xs font-black brutal-sm rounded-full px-3 py-1 bg-white">{t("Toon iedereen")}</button>
                </div>
              )}
              {(histFilter
                ? payouts.map((p) => ({ ...p, members: p.members.filter((m) => m.name === histFilter) })).filter((p) => p.members.length)
                : payouts
              ).map((p, i) => (
                <div key={i} data-testid={`payout-week-${i}`} className="brutal-sm rounded-xl bg-brand-cream p-3">
                  <div className="flex items-center justify-between font-bold text-sm mb-1.5">
                    <span>{t("Week van")} {p.week_start ? new Date(p.week_start).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "?"}</span>
                    <span className="font-black text-brand-violet">€ {(histFilter ? p.members.reduce((s, m) => s + Number(m.amount || 0), 0) : Number(p.total)).toFixed(2)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.members.map((m, j) => (
                      <button key={j} data-testid={`payout-member-${m.name}`} onClick={() => setHistFilter(histFilter === m.name ? null : m.name)}
                        className={`text-xs font-bold brutal-sm rounded-full px-2.5 py-0.5 transition-colors ${histFilter === m.name ? "bg-brand-violet text-white" : "bg-white hover:bg-brand-violet/10"}`}>{m.name}: € {Number(m.amount).toFixed(2)}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {data.wheel?.can_spin && (
        <div className="brutal rounded-2xl p-4 bg-brand-yellow flex items-center gap-3" data-testid="wheel-spin-card">
          <span className="text-3xl shrink-0">🎡</span>
          <div className="min-w-0 flex-1">
            <p className="font-head font-black text-lg leading-tight">{t("Je hebt gewonnen!")}</p>
            <p className="text-sm font-semibold text-brand-ink/70">{t("Draai het Rad van Fortuin voor je prijs.")}</p>
          </div>
          <button data-testid="wheel-open-btn" onClick={() => setWheelOpen(true)}
            className="brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-black bg-brand-violet text-white shrink-0">{t("Draai!")}</button>
        </div>
      )}

      {data.spin_credits > 0 && data.wheel?.enabled && (data.wheel?.prizes?.length > 1) && (
        <div className="brutal rounded-2xl p-4 bg-brand-violet text-white flex items-center gap-3" data-testid="credit-spin-card">
          <span className="text-3xl shrink-0">🎡</span>
          <div className="min-w-0 flex-1">
            <p className="font-head font-black text-lg leading-tight">{t("Je hebt")} {data.spin_credits} {data.spin_credits === 1 ? t("spin te goed") : t("spins te goed")}!</p>
            <p className="text-sm opacity-90">{t("Draai het rad en zie wat je wint.")}</p>
          </div>
          <button data-testid="credit-spin-btn" onClick={() => setCreditSpinOpen(true)}
            className="brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-black bg-white text-brand-violet shrink-0">{t("Draai!")}</button>
        </div>
      )}


      {admin && showCfg && (
        <div className="brutal rounded-xl bg-white p-4 space-y-4" data-testid="rewards-config">
          <label className="flex items-center justify-between gap-3">
            <span className="font-head font-bold">{t("Puntensysteem aan")}</span>
            <input type="checkbox" data-testid="rewards-enabled" className="w-6 h-6 accent-brand-violet"
              checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} />
          </label>
          <div>
            <p className="text-sm font-bold mb-2">{t("Reset")}</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="period-week" onClick={() => setCfg({ ...cfg, period: "week" })}
                className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${cfg.period === "week" ? "bg-brand-violet text-white" : "bg-white"}`}>{t("Wekelijks")}</button>
              <button data-testid="period-month" onClick={() => setCfg({ ...cfg, period: "month" })}
                className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${cfg.period === "month" ? "bg-brand-violet text-white" : "bg-white"}`}>{t("Maandelijks (1e)")}</button>
              <button data-testid="period-combi" onClick={() => setCfg({ ...cfg, period: "combi" })}
                className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${cfg.period === "combi" ? "bg-brand-violet text-white" : "bg-white"}`}>💶 {t("Combi (week + maand)")}</button>
            </div>
            {cfg.period === "combi" && (
              <p className="text-xs text-brand-ink/50 mt-2">{t("Punten tellen de hele maand door. Elke week kun je zakgeld verdienen, en aan het eind van de maand is er een extra prijs.")}</p>
            )}
          </div>
          {(cfg.period === "week" || cfg.period === "combi") && (
            <div>
              <p className="text-sm font-bold mb-2">{cfg.period === "combi" ? t("Weekstart op?") : t("Op welke dag?")}</p>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d, i) => (
                  <button key={i} data-testid={`weekday-${i}`} onClick={() => setCfg({ ...cfg, weekday: i })}
                    className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold capitalize ${cfg.weekday === i ? "bg-brand-ink text-white" : "bg-white"}`}>{t(d)}</button>
                ))}
              </div>
            </div>
          )}
          {cfg.period === "combi" && (
            <div className="border-t-2 border-dashed border-brand-ink/10 pt-3 space-y-3" data-testid="combi-editor">
              <div className="brutal-sm rounded-xl bg-brand-cream p-3 space-y-2">
                <p className="font-head font-bold text-sm flex items-center gap-1.5">💶 {t("Wekelijkse beloning")}</p>
                <input type="text" data-testid="combi-week-title" value={cfg.week_title} maxLength={60}
                  onChange={(e) => setCfg({ ...cfg, week_title: e.target.value })}
                  placeholder={t("Titel, bijv. Zakgeld of Weekbonus")}
                  className="w-full brutal-sm rounded-lg px-3 py-2 text-sm font-bold outline-none bg-white" />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-brand-ink/60">{t("Vanaf")}</span>
                  <input type="number" min="0" data-testid="combi-week-threshold" value={cfg.week_threshold}
                    onChange={(e) => setCfg({ ...cfg, week_threshold: parseInt(e.target.value || "0", 10) })}
                    className="w-24 brutal-sm rounded-lg px-2 py-1.5 text-sm font-bold outline-none text-center bg-white" />
                  <span className="text-xs font-bold text-brand-ink/60">{t("punten per week")}</span>
                </div>
                <input type="text" data-testid="combi-week-reward" value={cfg.week_reward} maxLength={200}
                  onChange={(e) => setCfg({ ...cfg, week_reward: e.target.value })}
                  placeholder={t("Beloning, bijv. €5")}
                  className="w-full brutal-sm rounded-lg px-3 py-2 text-sm font-medium outline-none bg-white" />
                <div className="flex items-center gap-2 flex-wrap border-t-2 border-dashed border-brand-ink/10 pt-2">
                  <span className="text-xs font-bold text-brand-ink/60">💶 {t("Zakgeldbedrag")}</span>
                  <span className="text-sm font-bold">€</span>
                  <input type="number" min="0" step="1" data-testid="combi-week-amount" value={cfg.week_allowance_amount || ""}
                    placeholder="0"
                    onChange={(e) => setCfg({ ...cfg, week_allowance_amount: e.target.value === "" ? 0 : Math.max(0, parseFloat(e.target.value) || 0) })}
                    className="w-24 brutal-sm rounded-lg px-2 py-1.5 text-sm font-bold outline-none text-center bg-white" />
                  <span className="text-xs font-bold text-brand-ink/60">{t("per week (pro-rato uitbetaald)")}</span>
                </div>
                {cfg.week_allowance_amount > 0 && (
                  <button type="button" data-testid="open-allowance-wizard" onClick={() => setWizardOpen(true)}
                    className="w-full brutal-sm brutal-press rounded-xl px-3 py-2.5 text-sm font-bold bg-brand-violet text-white flex items-center justify-center gap-1.5">
                    <Trophy className="w-4 h-4" strokeWidth={2.5} />{t("Zakgeld afronden & uitbetalen")}
                  </button>
                )}
              </div>
              <div className="brutal-sm rounded-xl bg-brand-cream p-3 space-y-2">
                <p className="font-head font-bold text-sm flex items-center gap-1.5">🏆 {t("Maandelijkse beloning")}</p>
                <input type="text" data-testid="combi-month-title" value={cfg.month_title} maxLength={60}
                  onChange={(e) => setCfg({ ...cfg, month_title: e.target.value })}
                  placeholder={t("Titel, bijv. Extra prijs of Maandbonus")}
                  className="w-full brutal-sm rounded-lg px-3 py-2 text-sm font-bold outline-none bg-white" />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-brand-ink/60">{t("Vanaf")}</span>
                  <input type="number" min="0" data-testid="combi-month-threshold" value={cfg.month_threshold}
                    onChange={(e) => setCfg({ ...cfg, month_threshold: parseInt(e.target.value || "0", 10) })}
                    className="w-24 brutal-sm rounded-lg px-2 py-1.5 text-sm font-bold outline-none text-center bg-white" />
                  <span className="text-xs font-bold text-brand-ink/60">{t("punten (0 = voor de #1)")}</span>
                </div>
                <input type="text" data-testid="combi-month-reward" value={cfg.month_reward} maxLength={200}
                  onChange={(e) => setCfg({ ...cfg, month_reward: e.target.value })}
                  placeholder={t("Beloning, bijv. dagje uit")}
                  className="w-full brutal-sm rounded-lg px-3 py-2 text-sm font-medium outline-none bg-white" />
              </div>
            </div>
          )}
          {cfg.period === "week" && (
            <div className="border-t-2 border-dashed border-brand-ink/10 pt-3" data-testid="week-allowance-editor">
              <div className="brutal-sm rounded-xl bg-brand-cream p-3 space-y-2">
                <p className="font-head font-bold text-sm flex items-center gap-1.5">💶 {t("Wekelijks zakgeld")}</p>
                <input type="text" data-testid="week-allowance-title" value={cfg.week_title} maxLength={60}
                  onChange={(e) => setCfg({ ...cfg, week_title: e.target.value })}
                  placeholder={t("Titel, bijv. Zakgeld of Weekbonus")}
                  className="w-full brutal-sm rounded-lg px-3 py-2 text-sm font-bold outline-none bg-white" />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-brand-ink/60">{t("Vanaf")}</span>
                  <input type="number" min="0" data-testid="week-allowance-threshold" value={cfg.week_threshold || ""}
                    placeholder="0"
                    onChange={(e) => setCfg({ ...cfg, week_threshold: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                    className="w-24 brutal-sm rounded-lg px-2 py-1.5 text-sm font-bold outline-none text-center bg-white" />
                  <span className="text-xs font-bold text-brand-ink/60">{t("punten per week")}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap border-t-2 border-dashed border-brand-ink/10 pt-2">
                  <span className="text-xs font-bold text-brand-ink/60">💶 {t("Zakgeldbedrag")}</span>
                  <span className="text-sm font-bold">€</span>
                  <input type="number" min="0" step="1" data-testid="week-allowance-amount" value={cfg.week_allowance_amount || ""}
                    placeholder="0"
                    onChange={(e) => setCfg({ ...cfg, week_allowance_amount: e.target.value === "" ? 0 : Math.max(0, parseFloat(e.target.value) || 0) })}
                    className="w-24 brutal-sm rounded-lg px-2 py-1.5 text-sm font-bold outline-none text-center bg-white" />
                  <span className="text-xs font-bold text-brand-ink/60">{t("per week (pro-rato uitbetaald)")}</span>
                </div>
                <p className="text-xs text-brand-ink/50">{t("Vul een bedrag in en sla op. Daarna keer je uit via de Zakgeld-knop in het menu.")}</p>
              </div>
            </div>
          )}
          <div className="brutal-sm rounded-xl bg-brand-cream p-3 text-xs font-semibold text-brand-ink/70 space-y-1">
            <p>✅ {t("Taak afvinken")}: +50 · 🙌 {t("Taak oppakken")}: +75</p>
            <p>🗳️ {t("Poll beantwoorden")}: +25 · ⏰ {t("Taak te laat")}: −100</p>
          </div>

          <div className="border-t-2 border-dashed border-brand-ink/10 pt-3" data-testid="participants-editor">
            <p className="font-head font-bold mb-1 flex items-center gap-1.5"><Users className="w-4 h-4 text-brand-violet" strokeWidth={2.5} />{t("Wie doet mee?")}</p>
            <p className="text-xs text-brand-ink/50 mb-2">{t("Bepaal wie punten verdient. Wie niet meedoet, krijgt géén punten en telt ook niet als 'gemist'.")}</p>
            <div className="flex flex-col gap-1.5">
              <button data-testid="participant-all" onClick={() => setCfg({ ...cfg, participant_mode: "all" })}
                className={`brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold text-left ${cfg.participant_mode === "all" ? "bg-brand-violet text-white" : "bg-white"}`}>👥 {t("Iedereen")}</button>
              <button data-testid="participant-non-admins" onClick={() => setCfg({ ...cfg, participant_mode: "non_admins" })}
                className={`brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold text-left ${cfg.participant_mode === "non_admins" ? "bg-brand-violet text-white" : "bg-white"}`}>🧒 {t("Sluit beheerders uit (alleen de rest)")}</button>
              <button data-testid="participant-group" onClick={() => setCfg({ ...cfg, participant_mode: "group" })}
                className={`brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold text-left ${cfg.participant_mode === "group" ? "bg-brand-violet text-white" : "bg-white"}`}>🏷️ {t("Alleen één groep")}</button>
            </div>
            {cfg.participant_mode === "group" && (
              <select data-testid="participant-group-select" value={cfg.participant_group || ""} onChange={(e) => setCfg({ ...cfg, participant_group: e.target.value || null })}
                className="w-full brutal-sm rounded-xl px-3 py-2 font-medium outline-none bg-white mt-2 text-sm">
                <option value="">{t("Kies een groep…")}</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            )}
          </div>

          <div className="border-t-2 border-dashed border-brand-ink/10 pt-3 space-y-2" data-testid="catchup-editor">
            <label className="flex items-center justify-between"><span className="font-bold text-sm flex items-center gap-1.5">🔁 {t("Inhaalklussen (Tweede kans)")}
                <InfoTip testid="info-catchup" text={t("Extra klusjes waarmee iemand die achterloopt punten kan inhalen. Voorbeeld: Sam heeft deze week weinig punten; hij pakt 'Ramen zemen = 100' uit de Klussen Pool en haalt zo zijn achterstand in. Zet je dit uit, dan zijn er geen inhaalklusjes.")} /></span>
              <input type="checkbox" data-testid="toggle-catchup" checked={cfg.catchup_enabled} onChange={(e) => setCfg({ ...cfg, catchup_enabled: e.target.checked })} className="w-5 h-5" /></label>
            <label className="flex items-center justify-between"><span className="font-bold text-sm flex items-center gap-1.5">📊 {t("Slimme drempel (% van haalbaar)")}
                <InfoTip testid="info-smart" text={t("De doelscore voor een beloning wordt niet vast, maar een percentage van wat écht haalbaar was (alle punten die deze periode te verdienen waren). Voorbeeld: er viel deze week 200 punten te halen en het percentage staat op 80% → de drempel wordt 160 punten. Een rustige week verlaagt zo automatisch de lat.")} /></span>
              <input type="checkbox" data-testid="toggle-smart" checked={cfg.smart_threshold} onChange={(e) => setCfg({ ...cfg, smart_threshold: e.target.checked })} className="w-5 h-5" /></label>
            {cfg.smart_threshold && <div className="flex items-center gap-2 text-sm"><span>{t("Percentage")}</span><input type="number" min="0" max="100" data-testid="smart-pct" value={cfg.smart_threshold_pct} onChange={(e) => setCfg({ ...cfg, smart_threshold_pct: Number(e.target.value) })} className="w-20 brutal-sm rounded-lg px-2 py-1" />%</div>}
            <div className="flex items-center gap-2 text-sm"><span className="flex items-center gap-1.5">{t("Drempel-verlaging bij afwezig")}
                <InfoTip testid="info-away" text={t("Was iemand een deel van de periode weg (ziek, logeren, vakantie)? Dan telt zijn drempel maar voor dit percentage mee, zodat afwezigheid niet oneerlijk telt. Voorbeeld: drempel is 160 punten en dit staat op 50% → voor die persoon wordt de drempel 80 punten. Op 100% verandert er niets.")} /></span><input type="number" min="0" max="100" data-testid="away-pct" value={cfg.away_reduction_pct} onChange={(e) => setCfg({ ...cfg, away_reduction_pct: Number(e.target.value) })} className="w-20 brutal-sm rounded-lg px-2 py-1" />%</div>
            <p className="text-xs font-bold text-brand-ink/60">{t("Klussen Pool — één per regel als 'Titel = punten'")}</p>
            <textarea data-testid="catchup-pool" rows={4} value={(cfg.catchup_pool || []).map((p) => `${p.title} = ${p.points || 0}`).join("\n")}
              onChange={(e) => setCfg({ ...cfg, catchup_pool: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [tt, pp] = l.split("="); return { title: (tt || "").trim(), points: parseInt((pp || "0").trim()) || 0 }; }).filter((x) => x.title) })}
              className="w-full brutal-sm rounded-lg px-3 py-2 text-sm" placeholder={"Ramen zemen = 100\nPlanten water geven = 20"} />
            <div className="flex items-center gap-2 text-sm flex-wrap border-t-2 border-dashed border-brand-ink/10 pt-2">
              <span className="flex items-center gap-1.5 font-bold text-brand-ink/70">🔔 {t("Herinnering vooraf")}
                <InfoTip testid="info-catchup-reminder" text={t("Hoeveel dagen vóór de reset krijgt iemand die zijn weekdoel nog niet haalde een duwtje om nog een inhaalklus te pakken. Bij 2 of meer dagen sturen we óók een extra 'laatste kans'-melding op de dag vóór de reset.")} /></span>
              <input type="number" min="1" max="6" data-testid="catchup-reminder-days" value={cfg.catchup_reminder_days ?? 1}
                onChange={(e) => setCfg({ ...cfg, catchup_reminder_days: Math.max(1, Math.min(6, parseInt(e.target.value || "1", 10) || 1)) })}
                className="w-16 brutal-sm rounded-lg px-2 py-1 text-sm font-bold text-center outline-none bg-white" />
              <span className="text-xs text-brand-ink/50">{t("dag(en) vóór de reset")}</span>
            </div>
          </div>

          <div className="border-t-2 border-dashed border-brand-ink/10 pt-3" data-testid="not-done-penalty-editor">
            <p className="text-sm font-bold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-brand-orange" strokeWidth={2.5} />{t("Strafpunten bij niet-uitgevoerde taak")}</p>
            <p className="text-xs text-brand-ink/50 mb-2">{t("Als een beheerder afvinkt dat iemand anders de taak moest oppakken, worden deze punten afgetrokken bij wie 'm liet liggen.")}</p>
            <div className="flex items-center gap-2">
              <span className="font-black text-brand-orange text-lg">−</span>
              <input type="number" min="0" data-testid="not-done-penalty-input" value={Math.abs(cfg.not_done_penalty || 0)}
                onChange={(e) => setCfg({ ...cfg, not_done_penalty: -Math.abs(parseInt(e.target.value || "0", 10)) })}
                className="w-28 brutal-sm rounded-xl px-3 py-2.5 text-sm font-bold outline-none bg-brand-cream text-center" />
              <span className="text-sm font-semibold text-brand-ink/50">{t("punten")}</span>
            </div>
          </div>

          <div className={`border-t-2 border-dashed border-brand-ink/10 pt-3 ${cfg.period === "combi" ? "hidden" : ""}`} data-testid="prizes-editor">
            <div className="flex items-center justify-between mb-1">
              <span className="font-head font-bold flex items-center gap-1.5"><Gift className="w-4 h-4 text-brand-violet" strokeWidth={2.5} />{t("Beloningen / prijzen")}</span>
              <button data-testid="add-prize-btn" onClick={addPrize} className="brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold bg-brand-violet text-white flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" strokeWidth={3} />{t("Prijs")}
              </button>
            </div>
            <p className="text-xs text-brand-ink/50 mb-2">{t("Koppel punten aan een échte beloning. Aan het einde van de periode zie je (en mailen we) wie 'm verdient.")}</p>
            <div className="space-y-2">
              {(cfg.prizes || []).map((p, i) => (
                <div key={i} data-testid={`prize-row-${i}`} className="brutal-sm rounded-xl bg-white p-2.5 space-y-2">
                  <div className="flex gap-1.5">
                    <button data-testid={`prize-type-winner-${i}`} onClick={() => updPrize(i, { type: "winner" })}
                      className={`flex-1 brutal-sm brutal-press rounded-full px-2 py-1.5 text-xs font-bold ${p.type === "winner" ? "bg-brand-yellow" : "bg-brand-cream"}`}>🥇 {t("Winnaar (#1)")}</button>
                    <button data-testid={`prize-type-threshold-${i}`} onClick={() => updPrize(i, { type: "threshold" })}
                      className={`flex-1 brutal-sm brutal-press rounded-full px-2 py-1.5 text-xs font-bold ${p.type === "threshold" ? "bg-brand-violet text-white" : "bg-brand-cream"}`}>🎯 {t("Vanaf punten")}</button>
                    <button data-testid={`prize-delete-${i}`} onClick={() => delPrize(i)} className="brutal-sm brutal-press rounded-full p-2 bg-white"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                  </div>
                  {p.type === "threshold" && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-brand-ink/60">{t("Vanaf")}</span>
                      <input type="number" min="0" data-testid={`prize-threshold-${i}`} value={p.threshold}
                        onChange={(e) => updPrize(i, { threshold: parseInt(e.target.value || "0", 10) })}
                        className="w-24 brutal-sm rounded-lg px-2 py-1.5 text-sm font-bold outline-none text-center" />
                      <span className="text-xs font-bold text-brand-ink/60">{t("punten")}</span>
                    </div>
                  )}
                  {p.type === "winner" && (
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-brand-ink/60">{t("Minimaal")}</span>
                        <input type="number" min="0" data-testid={`prize-minpoints-${i}`} value={p.min_points || 0}
                          onChange={(e) => updPrize(i, { min_points: parseInt(e.target.value || "0", 10) })}
                          className="w-24 brutal-sm rounded-lg px-2 py-1.5 text-sm font-bold outline-none text-center" />
                        <span className="text-xs font-bold text-brand-ink/60">{t("punten (anders geen winnaar)")}</span>
                      </div>
                      <p className="text-[11px] text-brand-ink/40 mt-1">{t("Laat op 0 staan om altijd een winnaar te hebben.")}</p>
                    </div>
                  )}
                  <input type="text" data-testid={`prize-desc-${i}`} value={p.description} maxLength={200}
                    onChange={(e) => updPrize(i, { description: e.target.value })}
                    placeholder={t("Beloning, bijv. €5 zakgeld of bioscoopkaartje")}
                    className="w-full brutal-sm rounded-lg px-3 py-2 text-sm font-medium outline-none" />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t-2 border-dashed border-brand-ink/10 pt-3" data-testid="wheel-editor">
            <label className="flex items-center justify-between gap-3">
              <span className="font-head font-bold flex items-center gap-1.5">🎡 {t("Rad van Fortuin")}</span>
              <input type="checkbox" data-testid="wheel-enabled" className="w-6 h-6 accent-brand-violet"
                checked={cfg.wheel_enabled} onChange={(e) => setCfg({ ...cfg, wheel_enabled: e.target.checked })} />
            </label>
            <p className="text-xs text-brand-ink/50 mt-1">{t("De winnaar(s) mogen een rad draaien voor een verrassingsprijs (niet bewaard, puur voor de lol).")}</p>
            {cfg.wheel_enabled && (
              <div className="mt-3 space-y-3">
                <div>
                  <p className="text-sm font-bold mb-1.5">{t("Wie krijgt de spin?")}</p>
                  <div className="flex flex-col gap-1.5">
                    <button data-testid="wheel-award-winner" onClick={() => setCfg({ ...cfg, wheel_award: "winner" })}
                      className={`brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold text-left ${cfg.wheel_award === "winner" ? "bg-brand-violet text-white" : "bg-white"}`}>🏆 {t("De winnaar(s)")}</button>
                    <button data-testid="wheel-award-threshold" onClick={() => setCfg({ ...cfg, wheel_award: "month_threshold" })}
                      className={`brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold text-left ${cfg.wheel_award === "month_threshold" ? "bg-brand-violet text-white" : "bg-white"}`}>🎯 {t("Iedereen die de maanddrempel haalt")}</button>
                  </div>
                  {cfg.wheel_award === "month_threshold" && cfg.period !== "combi" && (
                    <p className="text-xs font-semibold text-brand-orange mt-1.5">{t("Let op: deze optie werkt alleen met de Combi-periode (week + maand) én een ingestelde maanddrempel.")}</p>
                  )}
                </div>
                <div data-testid="wheel-bonus-spin-section">
                  <p className="text-sm font-bold mb-1.5">🎁 {t("Extra spin boven de maanddrempel")}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-brand-ink/60">{t("Bij")}</span>
                    <input type="number" min="0" data-testid="wheel-bonus-spin-points" value={cfg.month_bonus_spin_points || ""} placeholder="0" inputMode="numeric"
                      onChange={(e) => setCfg({ ...cfg, month_bonus_spin_points: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                      className="w-24 brutal-sm rounded-lg px-2 py-1.5 text-sm font-bold outline-none text-center bg-white" />
                    <span className="text-xs font-bold text-brand-ink/60">{t("punten boven de maanddrempel (0 = uit)")}</span>
                  </div>
                  <p className="text-xs text-brand-ink/50 mt-1">{t("Max. 1 extra spin per maand, toegekend op de resetdatum. Zo blijft het de moeite waard om door te gaan na het halen van je drempel.")}</p>
                  {cfg.month_bonus_spin_points > 0 && (cfg.period !== "combi" || !(cfg.month_threshold > 0) || !(cfg.wheel_prizes?.length > 0)) && (
                    <p className="text-xs font-semibold text-brand-orange mt-1.5" data-testid="wheel-bonus-spin-warning">{t("Let op: werkt alleen met de Combi-periode (week + maand), een ingestelde maanddrempel én minstens één radprijs.")}</p>
                  )}
                </div>
                {cfg.wheel_award === "winner" && (
                <div>
                  <p className="text-sm font-bold mb-1.5">{t("Wanneer?")}</p>
                  <div className="flex flex-col gap-1.5">
                    <button data-testid="wheel-mode-always" onClick={() => setCfg({ ...cfg, wheel_mode: "always" })}
                      className={`brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold text-left ${cfg.wheel_mode === "always" ? "bg-brand-violet text-white" : "bg-white"}`}>🏆 {t("Altijd voor de winnaar(s)")}</button>
                    <button data-testid="wheel-mode-tie" onClick={() => setCfg({ ...cfg, wheel_mode: "tie" })}
                      className={`brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold text-left ${cfg.wheel_mode === "tie" ? "bg-brand-violet text-white" : "bg-white"}`}>🤝 {t("Alleen bij gelijkstand")}</button>
                  </div>
                </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-bold">{t("Radprijzen")}</span>
                    <button data-testid="add-wheel-prize-btn" onClick={addWheelPrize} className="brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold bg-brand-violet text-white flex items-center gap-1">
                      <Plus className="w-3.5 h-3.5" strokeWidth={3} />{t("Prijs")}
                    </button>
                  </div>
                  <p className="text-xs text-brand-ink/50 mb-2">{t("Laat alles op 0 (= normaal) voor een eerlijk rad: iedereen even veel kans. Wil je een vakje vaker laten vallen? Zet de kans hoger (2, 3, …). Het percentage toont de échte kans.")}</p>
                  <div className="space-y-2">
                    {(cfg.wheel_prizes || []).map((w, i) => {
                      const eff = (p) => Math.max(1, p.weight || 0);
                      const totalW = (cfg.wheel_prizes || []).reduce((s, p) => s + eff(p), 0) || 1;
                      const pctW = Math.round((eff(w) / totalW) * 100);
                      return (
                      <div key={i} className="flex gap-1.5 items-center">
                        <input type="text" data-testid={`wheel-prize-${i}`} value={w.label || ""} maxLength={60}
                          onChange={(e) => updWheelPrize(i, { label: e.target.value })}
                          placeholder={t("Prijs op het rad")}
                          className="flex-1 min-w-0 brutal-sm rounded-lg px-3 py-2 text-sm font-medium outline-none" />
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-[11px] font-bold text-brand-ink/50">{t("kans")}</span>
                          <input type="number" min="0" data-testid={`wheel-prize-weight-${i}`} value={w.weight || 0}
                            onChange={(e) => updWheelPrize(i, { weight: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                            title={t("0 = normaal (gelijke kans). Hoger = vaker.")}
                            className="w-14 brutal-sm rounded-lg px-2 py-2 text-sm font-bold outline-none text-center" />
                          <span data-testid={`wheel-prize-pct-${i}`} className="text-xs font-black text-brand-violet w-9 text-right tabular-nums">{pctW}%</span>
                        </div>
                        <button data-testid={`wheel-prize-delete-${i}`} onClick={() => delWheelPrize(i)} className="brutal-sm brutal-press rounded-full p-2 bg-white shrink-0"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                      </div>
                      );
                    })}
                    {(!cfg.wheel_prizes || cfg.wheel_prizes.length === 0) && (
                      <p className="text-xs font-semibold text-brand-ink/40">{t("Nog geen radprijzen — voeg er minstens 2 toe.")}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div data-testid="rewards-autosave-status" className="flex items-center justify-center gap-2 text-sm font-bold py-1 text-brand-ink/50">
            {saveState === "saving" ? (
              <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />{t("Wijzigingen opslaan…")}</>
            ) : saveState === "saved" ? (
              <><Check className="w-4 h-4 text-brand-green" strokeWidth={3} /><span className="text-brand-green">{t("Automatisch opgeslagen")}</span></>
            ) : (
              <><Check className="w-4 h-4" strokeWidth={2.5} />{t("Wijzigingen worden automatisch opgeslagen")}</>
            )}
          </div>
        </div>
      )}

      {weekProg?.enabled && weekProg?.participating && (
        <div className="brutal rounded-xl bg-white p-4 space-y-3" data-testid="week-progress">
          <div className="flex items-center justify-between gap-2">
            <p className="font-head font-black text-lg">🎯 {t("Deze week")}</p>
            <button onClick={toggleAway} data-testid="toggle-away" className={`brutal-sm rounded-full px-3 py-1 text-xs font-bold shrink-0 ${weekProg.away ? "bg-brand-violet text-white" : "bg-brand-cream"}`}>{weekProg.away ? `${t("Afwezig gemeld")} 🌴` : t("Ik was (deels) afwezig")}</button>
          </div>
          <div className="h-3 rounded-full bg-brand-ink/10 overflow-hidden"><div className="h-full bg-brand-green transition-all" style={{ width: `${weekProg.threshold > 0 ? Math.min(100, Math.round((weekProg.points / weekProg.threshold) * 100)) : 100}%` }} /></div>
          <p className="text-sm font-bold">{weekProg.points} / {weekProg.threshold} {t("punten")} · {weekProg.title}</p>
          <p className="text-sm text-brand-ink/70">{weekProg.message}</p>
          {!weekProg.reached && weekProg.catchup_enabled && (weekProg.pool || []).length > 0 && (
            <div className="space-y-1.5 pt-1">
              <p className="text-xs font-bold text-brand-ink/60">🔁 {t("Inhaalklussen — pak er een op")}</p>
              {weekProg.pool.map((p, i) => (
                <button key={i} data-testid={`catchup-claim-${i}`} onClick={() => claimCatchup(p.title)} className="w-full brutal-sm brutal-press rounded-lg px-3 py-2 bg-brand-cream font-bold text-sm flex items-center justify-between"><span className="truncate">{p.title}</span><span className="text-brand-violet shrink-0 ml-2">+{p.points || 0}</span></button>
              ))}
            </div>
          )}
        </div>
      )}

      {!admin && myReflection && (
        <div className="brutal rounded-xl bg-white p-4" data-testid="my-reflection">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-head font-black text-lg flex items-center gap-2"><Users className="w-5 h-5 text-brand-violet" strokeWidth={2.5} />{t("Mijn spiegel")}</p>
            <div className="flex gap-1" data-testid="reflect-period-me">
              {[7, 30, 90].map((d) => (
                <button key={d} data-testid={`reflect-period-me-${d}`} onClick={() => changeReflectDays(d)}
                  className={`brutal-sm rounded-full px-3 py-1 text-xs font-black transition-colors ${reflectDays === d ? "bg-brand-violet text-white" : "bg-brand-cream text-brand-ink/70"}`}>{d} {t("dagen")}</button>
              ))}
            </div>
          </div>
          {myReflection.count > 0 ? (
            <p className="text-sm text-brand-ink/70 mt-2">{t("De afgelopen")} {myReflection.since_days} {t("dagen moest")} <span className="font-black">{myReflection.count}×</span> {t("een taak van jou door iemand anders worden opgepakt. Kleine reminder — je huisgenoten rekenen op je. 💛")}</p>
          ) : (
            <p className="text-sm text-brand-ink/70 mt-2">{t("De afgelopen")} {myReflection.since_days} {t("dagen heb je al je taken zelf opgepakt. Top gedaan! 🙌")}</p>
          )}
        </div>
      )}

      {admin && (
        <div className="brutal rounded-xl bg-white p-4 space-y-3" data-testid="reflection-mirror">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-head font-black text-lg flex items-center gap-2"><Users className="w-5 h-5 text-brand-violet" strokeWidth={2.5} />{t("Zachte spiegel")}</p>
            <div className="flex gap-1" data-testid="reflect-period-admin">
              {[7, 30, 90].map((d) => (
                <button key={d} data-testid={`reflect-period-admin-${d}`} onClick={() => changeReflectDays(d)}
                  className={`brutal-sm rounded-full px-3 py-1 text-xs font-black transition-colors ${reflectDays === d ? "bg-brand-violet text-white" : "bg-brand-cream text-brand-ink/70"}`}>{d} {t("dagen")}</button>
              ))}
            </div>
          </div>
          <p className="text-xs text-brand-ink/50">{t("Hoe vaak moest een taak de afgelopen")} {reflection.since_days} {t("dagen door iemand anders worden opgepakt. Bedoeld als vriendelijk spiegeltje — niet om af te rekenen. 💛")}</p>
          {(reflection.items || []).length === 0 ? (
            <p className="text-sm font-semibold text-brand-ink/40" data-testid="reflection-empty">{t("Iedereen pakt zijn taken netjes op. Top! 🙌")}</p>
          ) : (
            <div className="space-y-2">
              {reflection.items.map((it) => (
                <div key={it.user_id} data-testid={`reflection-${it.user_id}`} className="brutal-sm rounded-xl bg-brand-cream p-2.5 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-full bg-brand-violet/15 text-brand-violet font-black text-sm flex items-center justify-center shrink-0 overflow-hidden">
                    {it.avatar_url ? <img src={it.avatar_url} alt="" className="w-full h-full object-cover" /> : (it.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="font-bold text-sm truncate flex-1">{it.name}</span>
                  <span className="shrink-0 brutal-sm rounded-full px-3 py-1 text-xs font-black bg-white">{it.count}× {t("overgenomen")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {admin && data.enabled && (
        <div className="brutal rounded-xl bg-white p-4 space-y-4" data-testid="rewards-admin-manage">
          <p className="font-head font-black text-lg flex items-center gap-2"><Settings2 className="w-5 h-5 text-brand-violet" strokeWidth={2.5} />{t("Punten beheren")}</p>

          <div className="space-y-2" data-testid="manual-adjust">
            <p className="text-sm font-bold">{t("Handmatig punten toekennen of aftrekken")}</p>
            <select data-testid="adjust-user" value={adjUser} onChange={(e) => setAdjUser(e.target.value)}
              className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none bg-brand-cream text-sm">
              <option value="">{t("Kies een persoon…")}</option>
              {(data.standings || []).map((s) => <option key={s.user_id} value={s.user_id}>{s.name} ({s.points} pt)</option>)}
            </select>
            <div className="flex gap-2">
              <input type="number" data-testid="adjust-points" value={adjPoints} onChange={(e) => setAdjPoints(e.target.value)}
                placeholder={t("bv. 50 of -25")} className="w-28 brutal-sm rounded-xl px-3 py-2.5 text-sm font-bold outline-none bg-brand-cream text-center" />
              <input type="text" data-testid="adjust-reason" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} maxLength={200}
                placeholder={t("Reden (optioneel)")} className="flex-1 min-w-0 brutal-sm rounded-xl px-3 py-2.5 text-sm font-medium outline-none bg-brand-cream" />
            </div>
            <BrutalButton data-testid="adjust-submit" className="w-full" onClick={doAdjust}>
              <Plus className="w-4 h-4" strokeWidth={2.5} />{t("Punten toekennen")}
            </BrutalButton>
          </div>

          <div className="border-t-2 border-dashed border-brand-ink/10 pt-3" data-testid="penalties-editor">
            <p className="text-sm font-bold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-brand-yellow" strokeWidth={2.5} />{t("Te laat gekomen — alsnog volledige punten?")}</p>
            <p className="text-xs text-brand-ink/50 mb-2">{t("Verwijdert de −100 straf en geeft de volledige punten (+50) voor die taak.")}</p>
            {penalties.length === 0 ? (
              <p className="text-xs font-semibold text-brand-ink/40">{t("Geen te-laat-straffen deze periode. 🎉")}</p>
            ) : (
              <div className="space-y-2">
                {penalties.map((p) => (
                  <div key={p.event_id} data-testid={`penalty-${p.event_id}`} className="brutal-sm rounded-xl bg-brand-cream p-2.5 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm truncate">{p.user_name}</p>
                      <p className="text-xs text-brand-ink/50 truncate">{p.task_title}{p.ref_date ? ` · ${new Date(p.ref_date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}` : ""} · <span className="font-bold text-red-600">{p.points}</span></p>
                    </div>
                    <button data-testid={`forgive-${p.event_id}`} onClick={() => forgive(p)}
                      className="brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold bg-brand-green shrink-0 flex items-center gap-1">
                      <Check className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Volledige punten")}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t-2 border-dashed border-brand-ink/10 pt-3" data-testid="spin-manage">
            <p className="text-sm font-bold flex items-center gap-1.5">🎡 {t("Rad van Fortuin — spins toekennen")}</p>
            {data.wheel?.enabled ? (
              <>
                <p className="text-xs text-brand-ink/50 mb-2">{t("Geef iemand een draaibeurt. Diegene kan draaien in de app of op de kiosk (met PIN).")}</p>
                <div className="flex gap-2">
                  <select data-testid="grant-user" value={grantUser} onChange={(e) => setGrantUser(e.target.value)}
                    className="flex-1 min-w-0 brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none bg-brand-cream text-sm">
                    <option value="">{t("Kies een persoon…")}</option>
                    {(data.all_members || data.standings || []).map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
                  </select>
                  <button data-testid="grant-spin-btn" onClick={() => grantSpin(false)}
                    className="brutal-sm brutal-press rounded-xl px-3 py-2.5 text-sm font-bold bg-brand-violet text-white shrink-0 flex items-center gap-1"><Plus className="w-4 h-4" strokeWidth={2.5} />{t("Spin")}</button>
                </div>
                <button data-testid="grant-random-btn" onClick={() => grantSpin(true)}
                  className="mt-2 w-full brutal-sm brutal-press rounded-xl px-3 py-2.5 text-sm font-bold bg-brand-yellow flex items-center justify-center gap-1.5">🎲 {t("Willekeurig lid een spin geven")}</button>
              </>
            ) : (
              <p className="text-xs font-semibold text-brand-ink/40">{t("Zet eerst het Rad van Fortuin aan via het tandwiel en voeg radprijzen toe.")}</p>
            )}

            <div className="mt-4">
              <p className="text-sm font-bold flex items-center gap-1.5"><Trophy className="w-4 h-4 text-brand-violet" strokeWidth={2.5} />{t("Gewonnen op het rad")}</p>
              {spinResults.length === 0 ? (
                <p className="text-xs font-semibold text-brand-ink/40 mt-1">{t("Nog niemand heeft gedraaid.")}</p>
              ) : (
                <div className="space-y-2 mt-2" data-testid="spin-results">
                  {spinResults.map((r) => (
                    <div key={r.id} data-testid={`spin-result-${r.id}`} className={`brutal-sm rounded-xl p-2.5 flex items-center gap-2 ${r.claimed ? "bg-brand-cream opacity-70" : "bg-white"}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-sm truncate">{r.name} · <span className="text-brand-violet">🎁 {r.prize}</span></p>
                        <p className="text-xs text-brand-ink/50">{new Date(r.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}{r.source === "kiosk" ? " · kiosk" : ""}</p>
                      </div>
                      <button data-testid={`spin-claim-${r.id}`} onClick={() => toggleClaim(r.id)}
                        className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold shrink-0 flex items-center gap-1 ${r.claimed ? "bg-brand-green" : "bg-white"}`}>
                        <Check className="w-3.5 h-3.5" strokeWidth={2.5} />{r.claimed ? t("Uitgedeeld") : t("Markeer uitgedeeld")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!data.enabled ? (
        <EmptyState icon={Trophy} title={t("Nog niet actief")}
          text={admin ? t("Zet het puntensysteem aan via het tandwiel rechtsboven.") : t("De beheerder heeft het puntensysteem nog niet aangezet.")} />
      ) : data.participating === false ? (
        <>
          <div className="brutal rounded-2xl p-5 bg-brand-violet text-white" data-testid="not-participating">
            <p className="font-head font-black text-xl leading-tight flex items-center gap-2"><Users className="w-5 h-5" strokeWidth={2.5} />{t("Jij doet niet mee")}</p>
            <p className="text-sm opacity-90 mt-1">{t("Voor deze periode tel jij niet mee in het puntensysteem. Je verdient geen punten en mist ook niets — je kunt gewoon meekijken naar de ranglijst.")}</p>
          </div>
          <div>
            <p className="font-head font-black text-lg mb-3">{t("Ranglijst")}</p>
            <div className="space-y-2" data-testid="leaderboard-readonly">
              {data.standings.map((s) => {
                const clickable = admin || s.user_id === user?.user_id;
                return (
                <div key={s.user_id} onClick={() => clickable && openBreakdown(s)}
                  data-testid={`rank-readonly-${s.user_id}`}
                  className={`brutal-sm rounded-xl p-3 flex items-center gap-3 bg-white ${clickable ? "brutal-press cursor-pointer" : ""}`}>
                  <span className="font-head font-black text-lg w-8 text-center">{s.rank <= 3 ? MEDALS[s.rank - 1] : s.rank}</span>
                  <div className="w-10 h-10 rounded-full bg-brand-violet text-white flex items-center justify-center font-head font-black shrink-0 overflow-hidden">
                    {s.avatar_url ? <img src={s.avatar_url} alt="" className="w-full h-full object-cover" /> : (s.name || "?").charAt(0)}
                  </div>
                  <p className="flex-1 font-bold truncate">{s.name}</p>
                  <p className="font-head font-black text-xl">{s.points}</p>
                  {clickable && <ChevronRight className="w-4 h-4 text-brand-ink/30 shrink-0" strokeWidth={2.5} />}
                </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="brutal rounded-2xl p-5 bg-brand-violet text-white" data-testid="my-rewards">
            <p className="text-sm font-semibold opacity-80">{t("Jouw punten deze periode")}</p>
            <p className="font-head font-black text-5xl leading-none mt-1" data-testid="my-points">{data.me.points}</p>
            {periodFrom && periodTo && (
              <div className="inline-flex items-center gap-1.5 mt-2 bg-white/15 rounded-full px-3 py-1.5" data-testid="period-range">
                <Trophy className="w-3.5 h-3.5" strokeWidth={2.5} />
                <span className="text-xs font-bold">{t("Telt van")} {periodFrom} {t("t/m")} {periodTo}</span>
              </div>
            )}
            {data.period === "combi" && (
              <div className="mt-4 space-y-2" data-testid="combi-me">
                <div className="bg-white/15 rounded-xl p-3" data-testid="combi-me-week">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold flex items-center gap-1.5">💶 {data.week_title || t("Deze week")}{data.week_reward ? ` · ${data.week_reward}` : ""}</span>
                    <span className="text-xs font-bold">{data.me.week_earned ? "✅ " + t("verdiend") : "❌ " + t("nog niet")}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-2.5 rounded-full bg-white/25 overflow-hidden">
                      <div className="h-full bg-white rounded-full transition-all" style={{ width: `${data.me.week_earned ? 100 : (data.week_threshold > 0 ? Math.max(0, Math.min(99, Math.floor((data.me.week_points / data.week_threshold) * 100))) : 0)}%` }} />
                    </div>
                    <span className="text-xs font-bold shrink-0">{data.me.week_points}/{data.week_threshold} {t("pt")}</span>
                  </div>
                </div>
                <div className="bg-white/15 rounded-xl p-3" data-testid="combi-me-month">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold flex items-center gap-1.5">🏆 {data.month_title || t("Deze maand")}{data.month_reward ? ` · ${data.month_reward}` : ""}</span>
                    {data.month_threshold > 0 && <span className="text-xs font-bold">{data.me.month_points >= data.month_threshold ? "✅ " + t("gehaald") : "❌ " + t("nog niet")}</span>}
                  </div>
                  {data.month_threshold > 0 ? (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-2.5 rounded-full bg-white/25 overflow-hidden">
                        <div className="h-full bg-white rounded-full transition-all" style={{ width: `${data.me.month_points >= data.month_threshold ? 100 : Math.max(0, Math.min(99, Math.floor((data.me.month_points / data.month_threshold) * 100)))}%` }} />
                      </div>
                      <span className="text-xs font-bold shrink-0">{data.me.month_points}/{data.month_threshold} {t("pt")}</span>
                    </div>
                  ) : (
                    <p className="text-xs opacity-80 mt-1">{t("Extra prijs voor de nummer 1 aan het eind van de maand.")}</p>
                  )}
                  {data.me.bonus_spin && (
                    <p className="text-xs font-bold mt-2" data-testid="combi-me-bonus-spin">
                      🎡 {data.me.bonus_spin.earned
                        ? t("Extra spin binnen! Je krijgt hem op de resetdatum.")
                        : `${t("Extra spin: nog")} ${data.me.bonus_spin.needed} ${t("punten")} (${data.me.bonus_spin.points}/${data.me.bonus_spin.target})`}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="mt-4 bg-white/15 rounded-xl p-3">
              <div className="flex items-center gap-2 text-sm font-bold"><Sparkles className="w-4 h-4" strokeWidth={2.5} />{t("AI-inschatting")}</div>
              <p className="text-xs opacity-90 mt-1">{t("Je haalde")} {data.me.pct}% {t("van wat mogelijk was")} ({data.me.potential} {t("punten")}).</p>
              <div className="mt-2 h-2.5 rounded-full bg-white/25 overflow-hidden">
                <div className="h-full bg-white rounded-full transition-all" style={{ width: `${Math.max(0, Math.min(100, data.me.pct))}%` }} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.entries(KIND_META).filter(([k]) => k !== "manual_adjust").map(([k, m]) => (
                <span key={k} className="text-xs font-bold bg-white/15 rounded-full px-2.5 py-1">
                  {m.pts.startsWith("+") ? "✓" : "⏰"} {t(m.label)}: {data.me.breakdown?.[k] || 0}×
                </span>
              ))}
            </div>
          </div>

          {data.prizes && data.prizes.length > 0 && (
            <div className="brutal rounded-2xl bg-white p-4" data-testid="prizes-block">
              <p className="font-head font-black text-lg mb-3 flex items-center gap-2"><Gift className="w-5 h-5 text-brand-violet" strokeWidth={2.5} />{t("Te winnen beloningen")}</p>
              <div className="space-y-2">
                {data.prizes.map((p, i) => (
                  <div key={i} data-testid={`prize-display-${i}`} className="brutal-sm rounded-xl bg-brand-cream p-3 flex items-start gap-3">
                    <span className="text-xl shrink-0">{p.type === "winner" ? "🥇" : "🎯"}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm">{p.description}</p>
                      <p className="text-xs text-brand-ink/50">{p.type === "winner" ? (p.min_points > 0 ? `${t("Voor de nummer 1 (min.")} ${p.min_points} ${t("punten)")}` : t("Voor de nummer 1")) : `${t("Vanaf")} ${p.threshold} ${t("punten")}`}</p>
                      {p.winners && p.winners.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {p.winners.map((w) => (
                            <span key={w} className="text-xs font-bold bg-brand-green rounded-full px-2 py-0.5 text-brand-ink flex items-center gap-1"><Trophy className="w-3 h-3" strokeWidth={2.5} />{w}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="font-head font-black text-lg mb-3">{t("Ranglijst")}</p>
            {data.last_period?.winner_name && (
              <div className="brutal-sm rounded-xl bg-brand-yellow p-3 mb-3 flex items-center gap-2" data-testid="last-winner-banner">
                <span className="text-xl shrink-0">🏆</span>
                <p className="text-sm font-bold min-w-0">
                  {(data.last_period.winner_names?.length > 1 ? t("Winnaars vorige periode") : t("Winnaar vorige periode"))}: {(data.last_period.winner_names && data.last_period.winner_names.length ? data.last_period.winner_names.join(", ") : data.last_period.winner_name)}
                  {data.last_period.prizes_won?.length > 0 && (
                    <span className="font-semibold text-brand-ink/70"> · {t("won")} {data.last_period.prizes_won.join(", ")}</span>
                  )}
                </p>
              </div>
            )}
            <div className="space-y-2" data-testid="leaderboard">
              {data.standings.map((s) => {
                const mine = s.user_id === user?.user_id;
                const clickable = admin || mine;
                return (
                  <div key={s.user_id} data-testid={`rank-${s.user_id}`} onClick={() => clickable && openBreakdown(s)}
                    className={`brutal-sm rounded-xl p-3 flex items-center gap-3 ${mine ? "bg-brand-yellow" : "bg-white"} ${clickable ? "brutal-press cursor-pointer" : ""}`}>
                    <span className="font-head font-black text-lg w-8 text-center">{s.rank <= 3 ? MEDALS[s.rank - 1] : s.rank}</span>
                    <div className="w-10 h-10 rounded-full bg-brand-violet text-white flex items-center justify-center font-head font-black shrink-0 overflow-hidden">
                      {s.avatar_url ? <img src={s.avatar_url} alt="" className="w-full h-full object-cover" /> : (s.name || "?").charAt(0)}
                    </div>
                    <p className="flex-1 font-bold truncate">{s.name}{mine && <span className="text-xs font-semibold text-brand-ink/50"> · {t("jij")}</span>}</p>
                    {data.period === "combi" && s.week_earned && (
                      <span data-testid={`week-earned-${s.user_id}`} title={`${data.week_title || t("Zakgeld")} ${t("deze week verdiend")}`} className="text-base shrink-0">💶</span>
                    )}
                    {(data.last_period?.winner_ids?.includes(s.user_id) || data.last_period?.winner_id === s.user_id) && (
                      <span data-testid={`last-winner-badge-${s.user_id}`} title={t("Winnaar vorige periode")} className="text-lg shrink-0">🏆</span>
                    )}
                    <p className="font-head font-black text-xl">{s.points}</p>
                    {clickable && <ChevronRight className="w-4 h-4 text-brand-ink/30 shrink-0" strokeWidth={2.5} />}
                  </div>
                );
              })}
            </div>
          </div>

          {winlog.length > 0 && (
            <div className="brutal rounded-2xl bg-white p-4" data-testid="winlog-section">
              <p className="font-head font-black text-lg mb-1 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-brand-violet" strokeWidth={2.5} />{t("Wint-logboek")}
              </p>
              <p className="text-xs text-brand-ink/50 mb-3">{t("Wie won wat en wanneer — laatste 12 maanden.")}</p>
              <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="winlog-list">
                {winlog.map((w) => (
                  <div key={w.id} data-testid={`winlog-item-${w.id}`} className="brutal-sm rounded-xl bg-brand-cream p-3 flex items-center gap-3">
                    <span className="text-xl shrink-0">{WINLOG_ICON[w.kind] || "⭐"}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm truncate">{w.name}
                        {w.detail && <span className="font-semibold text-brand-ink/60"> · {w.detail}</span>}
                      </p>
                      <p className="text-xs text-brand-ink/45">
                        {new Date(w.created_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })}
                        {" · "}
                        {new Date(w.created_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
          data-testid="breakdown-dialog" onClick={() => setDetail(null)}>
          <div className="brutal bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-4 bg-brand-violet text-white flex items-start gap-3 shrink-0">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold opacity-80">{t("Puntenopbouw")}</p>
                <p className="font-head font-black text-xl leading-tight truncate" data-testid="breakdown-name">{detail.name}</p>
                {detail.period_start && detail.next_reset && (
                  <p className="text-xs opacity-90 mt-1">{t("Telt van")} {fmtDay(detail.period_start)} {t("t/m")} {dayBefore(detail.next_reset)}</p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="font-head font-black text-3xl leading-none" data-testid="breakdown-total">{detail.total}</p>
                <p className="text-xs opacity-80">{t("punten")}</p>
              </div>
              <button data-testid="breakdown-close" onClick={() => setDetail(null)}
                className="brutal-sm brutal-press rounded-full p-1.5 bg-white text-brand-ink shrink-0">
                <X className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2" data-testid="breakdown-list">
              {detail.combi && (
                <div className="space-y-2 mb-1" data-testid="breakdown-combi">
                  <div className="brutal-sm rounded-xl bg-brand-yellow p-3" data-testid="breakdown-combi-week">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-head font-black text-sm flex items-center gap-1.5">💶 {detail.combi.week_title || t("Deze week")}{detail.combi.week_reward ? ` · ${detail.combi.week_reward}` : ""}</span>
                      <span className="text-xs font-bold">{detail.combi.week_earned ? "✅ " + t("verdiend") : "❌ " + t("nog niet")}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-2.5 rounded-full bg-white overflow-hidden brutal-sm">
                        <div className="h-full bg-brand-violet rounded-full transition-all" style={{ width: `${detail.combi.week_earned ? 100 : (detail.combi.week_threshold > 0 ? Math.max(0, Math.min(99, Math.floor((detail.combi.week_points / detail.combi.week_threshold) * 100))) : 0)}%` }} />
                      </div>
                      <span className="text-xs font-bold shrink-0">{detail.combi.week_points}/{detail.combi.week_threshold} {t("pt")}</span>
                    </div>
                  </div>
                  <div className="brutal-sm rounded-xl bg-brand-cream p-3" data-testid="breakdown-combi-month">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-head font-black text-sm flex items-center gap-1.5">🏆 {detail.combi.month_title || t("Deze maand")}{detail.combi.month_reward ? ` · ${detail.combi.month_reward}` : ""}</span>
                      {!detail.combi.month_is_winner_prize && <span className="text-xs font-bold">{detail.combi.month_earned ? "✅ " + t("gehaald") : "❌ " + t("nog niet")}</span>}
                    </div>
                    {detail.combi.month_is_winner_prize ? (
                      <p className="text-xs text-brand-ink/50 mt-1">{t("Extra prijs voor de nummer 1 aan het eind van de maand.")}</p>
                    ) : (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-2.5 rounded-full bg-white overflow-hidden brutal-sm">
                          <div className="h-full bg-brand-violet rounded-full transition-all" style={{ width: `${detail.combi.month_earned ? 100 : Math.max(0, Math.min(99, Math.floor((detail.combi.month_points / detail.combi.month_threshold) * 100)))}%` }} />
                        </div>
                        <span className="text-xs font-bold shrink-0">{detail.combi.month_points}/{detail.combi.month_threshold} {t("pt")}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {detail.prizes_now && detail.prizes_now.length > 0 && (
                <div className="brutal-sm rounded-xl bg-brand-yellow p-3 space-y-1.5 mb-1" data-testid="breakdown-prizes">
                  <p className="font-head font-black text-sm flex items-center gap-1.5"><Gift className="w-4 h-4" strokeWidth={2.5} />{t("Verdient nu")}</p>
                  {detail.prizes_now.map((p, i) => (
                    <div key={i} data-testid={`breakdown-prize-${i}`} className="flex items-start gap-2 text-sm">
                      <span className="shrink-0">{p.type === "winner" ? "🥇" : "🎯"}</span>
                      <p className="font-bold leading-tight min-w-0">
                        {p.description}
                        <span className="font-semibold text-brand-ink/60"> · {p.type === "winner" ? t("koppositie") : `${t("vanaf")} ${p.threshold} ${t("punten")}`}</span>
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {detail.prizes_next && detail.prizes_next.length > 0 && (
                <div className="brutal-sm rounded-xl bg-brand-cream p-3 space-y-1.5 mb-1" data-testid="breakdown-prizes-next">
                  <p className="font-head font-black text-sm flex items-center gap-1.5"><Trophy className="w-4 h-4 text-brand-violet" strokeWidth={2.5} />{t("Nog te gaan")}</p>
                  {detail.prizes_next.map((p, i) => {
                    const target = p.type === "winner" ? p.min_points : p.threshold;
                    const pct = target > 0 ? Math.max(0, Math.min(100, Math.round((detail.total / target) * 100))) : 0;
                    return (
                    <div key={i} data-testid={`breakdown-prize-next-${i}`} className="flex items-start gap-2 text-sm">
                      <span className="shrink-0">{p.type === "winner" ? "🥇" : "🎯"}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold leading-tight">
                          {p.description}
                          <span className="font-semibold text-brand-violet"> · {t("nog")} {p.remaining} {t("punten te gaan")}</span>
                        </p>
                        <div className="mt-1 flex items-center gap-2">
                          <div className="flex-1 h-2 rounded-full bg-white overflow-hidden brutal-sm">
                            <div className="h-full bg-brand-violet rounded-full transition-all" style={{ width: `${pct}%` }} data-testid={`breakdown-prize-next-bar-${i}`} />
                          </div>
                          <span className="text-[11px] font-bold text-brand-ink/50 shrink-0">{detail.total}/{target}</span>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
              {detailLoading || detail.events === null ? (
                <p className="text-center text-brand-ink/50 py-6">{t("Laden…")}</p>
              ) : detail.events.length === 0 ? (
                <EmptyState icon={Trophy} title={t("Nog geen punten")}
                  text={t("Deze periode zijn er nog geen punten verdiend of afgetrokken.")} />
              ) : (
                detail.events.map((ev, i) => {
                  const m = KIND_META[ev.kind] || KIND_META.manual_adjust;
                  const Icon = m.icon;
                  const pos = ev.points >= 0;
                  const d = new Date(ev.created_at);
                  let dateLabel = d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
                  dateLabel = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);
                  const prev = i > 0 ? new Date(detail.events[i - 1].created_at) : null;
                  const newDay = !prev || prev.toDateString() !== d.toDateString();
                  return (
                    <div key={i}>
                      {newDay && <p className="text-xs font-head font-black uppercase tracking-wide text-brand-ink/40 mt-4 mb-1.5 first:mt-0">{dateLabel}</p>}
                      <div className="flex gap-3">
                        <div className="flex flex-col items-center pt-3.5">
                          <span className={`w-3 h-3 rounded-full shrink-0 ${pos ? "bg-emerald-500" : "bg-red-500"}`} />
                          {i < detail.events.length - 1 && <span className="flex-1 w-0.5 mt-1 rounded-full bg-brand-ink/10" />}
                        </div>
                        <div data-testid={`breakdown-row-${i}`} className="flex-1 brutal-sm rounded-xl bg-brand-cream p-3 flex items-center gap-3 mb-2">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${m.color}`}>
                            <Icon className="w-4.5 h-4.5" strokeWidth={2.5} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-bold text-sm truncate">{ev.ref || t(m.label)}</p>
                            <p className="text-xs text-brand-ink/50">{t(m.label)} · {fmtTime(ev.created_at)}</p>
                            <p className="text-[11px] font-black text-brand-violet mt-0.5">→ {t("saldo")} {ev.balance}</p>
                            {ev.kind === "task_overdue" && ev.deadline && (
                              <p className="text-xs text-red-600/80 font-semibold" data-testid={`breakdown-deadline-${i}`}>⏰ {ev.deadline}</p>
                            )}
                            {ev.forgiven > 0 && (
                              <p className="text-xs text-amber-600 font-bold" data-testid={`breakdown-forgiven-${i}`}>💛 {ev.forgiven} {ev.forgiven === 1 ? t("strafpunt kwijtgescholden") : t("strafpunten kwijtgescholden")}</p>
                            )}
                          </div>
                          <p className={`font-head font-black text-lg shrink-0 ${pos ? "text-emerald-600" : "text-red-600"}`}>
                            {pos ? "+" : ""}{ev.points}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {detail.forgiven_total > 0 && (
                <div data-testid="breakdown-forgiven-total" className="brutal-sm rounded-xl bg-amber-50 border-2 border-amber-200 p-3 mt-2 flex items-center gap-2">
                  <span className="text-lg">💛</span>
                  <p className="text-sm font-bold text-amber-700">{t("In totaal")} {detail.forgiven_total} {t("strafpunten kwijtgescholden — je saldo werd nooit negatief")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <WheelOfFortune open={wheelOpen} prizes={data.wheel?.prizes || []} onClose={() => setWheelOpen(false)} />
      <WheelOfFortune open={creditSpinOpen} prizes={data.wheel?.prizes || []}
        subtitle={t("Je hebt") + " " + (data.spin_credits || 0) + " " + t("te goed")}
        userId={user?.user_id}
        onResult={(prize) => doCreditSpin(prize)} onClose={() => setCreditSpinOpen(false)} />
    </div>
  );
}
